/*
 * The three publishing actions, and the one thing about them that surprises people.
 *
 * There is exactly one visibility gate: kb.Article.IsVisible. So "publish
 * immediately" has a single, complete meaning - IsVisible = 1 and PublishAtUtc =
 * now - and this panel owns both of those columns and nothing else.
 *
 * What it deliberately does NOT own is whether any text exists. The publishing rule
 * has a third condition, "a revision with IsCurrent = 1 exists", and that one is
 * the editor's business: it is settled by writing and saving, not by a publishing
 * option. The consequence is the surprise worth naming on screen - an article can
 * have both gates open and still show nothing to a reader, because nobody has
 * written it. That is the gap between deriveArticleStatus (what the author asked
 * for) and article.status (what a reader gets), and it is why the panel reads both.
 *
 * The publish date is entered in local time and stored as UTC, and the help
 * tooltip beside the label says so. That sentence used to sit permanently under
 * the input together with the resolved UTC instant echoed back - three lines of
 * explanation under a single control, in a panel that also carries a status badge,
 * a date line, two buttons and sometimes a warning notice.
 *
 * WHAT MOVED AND WHAT DID NOT. The explanation is in the tooltip. The echoed UTC
 * instant is gone rather than moved, on request; the consequence, stated so it is
 * not rediscovered, is that a timezone slip is no longer visible BEFORE saving -
 * what catches it afterwards is the "Publish date and time" line at the top of
 * this panel, which renders the stored instant back in local time. The
 * already-passed warning stayed on the page, and the comment above it says why:
 * it is a consequence of what was just typed, not a description of the field, and
 * a tooltip is unreadable at the moment such a warning appears.
 *
 * NOT ONLY ARTICLES (S42, 2026-10-12). A release note has the same two gates,
 * the same third condition and the same three actions on its own row
 * (kb.ReleaseNote), so it gets this panel rather than a copy of it. The panel
 * takes the row's publishing state as `entity`, the act as `onAction` - the page
 * makes the call and takes the answer - and a `noun` for the sentences. `article`
 * plus `onUpdated` remain the article form, with every string as it was.
 *
 * Its words come from the chosen language's dictionary, `t.admin.articles` (S48).
 * The sentences that name the thing take `noun` whole, as given.
 */

import { useState } from 'react';
import type { AdminArticleDetail, AdminArticleListItem, PublishActionInput } from '@accotool-kb/shared';
import {
  deriveArticleStatus,
  fromLocalDateTimeInputValue,
  toLocalDateTimeInputValue,
} from '@accotool-kb/shared';
import { useAdminApi } from '../../auth/AuthProvider.js';
import { useAction } from '../../hooks/useAsync.js';
import { DateTime, ErrorBanner, HelpTooltip, PublishingStatusBadge } from '../../components/ui.js';
import { useMessages } from '../../i18n/language.js';
import { richText } from '../../i18n/richText.js';

/**
 * What the panel reads of the row it publishes: the two gates, the verdict a
 * reader gets, and the concurrency token every action is sent under. An article's
 * and a release note's rows both carry exactly these.
 */
export type PublishableState = Pick<
  AdminArticleListItem,
  'isVisible' | 'publishAtUtc' | 'status' | 'rowVersion'
>;

/**
 * The article form (`article` + `onUpdated`), or any publishable row (`entity` +
 * `onAction` + `noun`). In the second form `onAction` performs the act and hands
 * the answer to the page; a rejection is shown here as the panel's error.
 */
type PublishControlsProps =
  | {
      article: AdminArticleDetail;
      onUpdated: (next: AdminArticleDetail) => void;
      entity?: undefined;
      onAction?: undefined;
      noun?: undefined;
    }
  | {
      entity: PublishableState;
      onAction: (body: PublishActionInput) => Promise<unknown>;
      /** Lower case, mid-sentence: 'release note'. */
      noun: string;
      article?: undefined;
      onUpdated?: undefined;
    };

export function PublishControls(props: PublishControlsProps): JSX.Element {
  const t = useMessages();
  const words = t.admin.articles.publishing;
  const api = useAdminApi();
  const action = useAction();

  const entity: PublishableState = props.article ?? props.entity;
  const noun = props.noun ?? 'article';

  const [scheduleAt, setScheduleAt] = useState(() =>
    toLocalDateTimeInputValue(entity.publishAtUtc),
  );
  // A flag rather than the sentence, so the sentence follows the language.
  const [scheduleError, setScheduleError] = useState(false);

  // What the two editorial gates say, ignoring whether anything is written. When
  // this reads 'published' and entity.status reads 'unwritten', the gates are open
  // and the row is empty - the one case worth explaining.
  const intent = deriveArticleStatus(entity.isVisible, entity.publishAtUtc);

  const run = async (body: PublishActionInput) => {
    if (props.article) {
      const { article, onUpdated } = props;
      const next = await action.run(() => api.publishAction(article.articleId, body));
      if (next) onUpdated(next);
      return;
    }
    const { onAction } = props;
    await action.run(() => onAction(body));
  };

  const publishNow = () => void run({ action: 'publish-now', rowVersion: entity.rowVersion });

  const schedule = () => {
    const publishAtUtc = fromLocalDateTimeInputValue(scheduleAt);
    if (!publishAtUtc) {
      setScheduleError(true);
      return;
    }
    setScheduleError(false);
    void run({ action: 'schedule', publishAtUtc, rowVersion: entity.rowVersion });
  };

  const hide = () => void run({ action: 'hide', rowVersion: entity.rowVersion });

  const scheduledInPast = scheduleAt !== '' && new Date(scheduleAt).getTime() <= Date.now();

  return (
    <div className="card">
      <div className="card-header">
        <h3>{t.admin.articles.shared.publishing}</h3>
        <PublishingStatusBadge status={entity.status} noun={noun} />
      </div>

      {action.error && <ErrorBanner error={action.error} onDismiss={action.clearError} />}

      <p className="faint">
        {richText(entity.status === 'scheduled' ? words.publishDateScheduled : words.publishDate, {
          date: <DateTime value={entity.publishAtUtc} />,
        })}
      </p>

      {intent === 'published' && entity.status === 'unwritten' && (
        <div className="notice notice-warning">
          <div className="notice-body">
            <strong>{words.unwritten}</strong>
            <p style={{ marginBottom: 0 }}>{words.unwrittenHelp(noun)}</p>
          </div>
        </div>
      )}

      <div className="button-row" style={{ marginBottom: '1rem' }}>
        <button
          type="button"
          className="button button-primary"
          onClick={publishNow}
          disabled={action.running}
          title={words.publishNowTitle(noun)}
        >
          {words.publishNow}
        </button>

        <button
          type="button"
          className="button"
          onClick={hide}
          disabled={action.running || entity.status === 'hidden'}
          title={words.hideTitle}
        >
          {words.hide}
        </button>
      </div>

      <fieldset
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          padding: 'var(--space-3)',
          margin: 0,
        }}
      >
        <legend style={{ fontSize: '0.875rem', fontWeight: 600, padding: '0 4px' }}>
          {words.scheduleLegend}
        </legend>

        <div className="field" style={{ marginBottom: '0.5rem' }}>
          <div className="field-label-row">
            <label htmlFor="publish-at">{t.admin.articles.shared.publishAtLocal}</label>
            <HelpTooltip label={words.howStored}>
              <p>{words.howStoredBody}</p>
            </HelpTooltip>
          </div>
          <input
            id="publish-at"
            type="datetime-local"
            value={scheduleAt}
            onChange={(event) => {
              setScheduleAt(event.target.value);
              setScheduleError(false);
            }}
          />
          {/*
            The error stays on the page, and so does the already-passed warning
            below. Both are consequences of what was just typed and have to be
            seen without going looking for them, which is exactly what separates
            them from the explanation in the tooltip above.
          */}
          {scheduleError && (
            <p className="field-error" role="alert">
              {words.chooseDateTime}
            </p>
          )}
        </div>

        {/*
          Deliberately NOT in the tooltip, though it reads like more of the same
          explanation.
          Saving a past instant sets IsVisible = 1 and backdates PublishAtUtc, so
          the article goes live at once - and this is the only thing on the panel
          that says so; the caption below changes to "Set publish date" but not
          why. A tooltip could not carry it: editing the date puts the pointer on
          the input, so the bubble is shut at exactly the moment the warning
          appears, and the trigger sits before the input in tab order, so a
          screen reader has already passed it.
        */}
        {scheduledInPast && (
          <p className="faint" style={{ marginTop: 0 }}>
            {words.alreadyPassed(noun)}
          </p>
        )}

        <button type="button" className="button" onClick={schedule} disabled={action.running}>
          {scheduledInPast ? words.setPublishDate : words.schedule}
        </button>
      </fieldset>
    </div>
  );
}
